import React, { useContext, useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Spinner, Form } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { Context } from '../index';
import { useToast } from '../contexts/ToastContext';
import { FaEnvelope, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { resendVerification, verifyEmail } from '../http/userAPI';

const VerifyEmail = () => {
    const { user } = useContext(Context);
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const [isResending, setIsResending] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState(null); // 'success', 'error', 'already-verified'
    const [errorMessage, setErrorMessage] = useState('');
    const [manualEmail, setManualEmail] = useState('');
    const emailToUse = user.user.email || manualEmail;

    useEffect(() => {
        const verifyToken = async () => {
            const token = new URLSearchParams(location.search).get('token');
            if (token) {
                try {
                    setIsVerifying(true);
                    setVerificationStatus(null);
                    setErrorMessage('');
                    
                    const result = await verifyEmail(token);
                    
                    if (result.alreadyVerified) {
                        setVerificationStatus('already-verified');
                        showToast('Email уже подтвержден!', 'info');
                    } else if (result.success) {
                        setVerificationStatus('success');
                        await user.refreshUser();
                        showToast('Email успешно подтвержден!', 'success');
                    }
                } catch (error) {
                    console.error('Ошибка верификации:', error);
                    setVerificationStatus('error');
                    const errorMsg = error.response?.data?.message || 'Ошибка при подтверждении email';
                    setErrorMessage(errorMsg);
                    showToast(errorMsg, 'error');
                } finally {
                    setIsVerifying(false);
                }
            }
        };

        verifyToken();
    }, [location.search, user, navigate, showToast]);

    const handleResendVerification = async () => {
        // Use manualEmail if entered, otherwise use user email
        const email = manualEmail.trim() || emailToUse;
        if (!email) {
            showToast('Пожалуйста, введите email для повторной отправки письма', 'error');
            return;
        }
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showToast('Пожалуйста, введите корректный email адрес', 'error');
            return;
        }
        
        setIsResending(true);
        try {
            await resendVerification(email);
            showToast('Письмо с подтверждением отправлено повторно', 'success');
        } catch (error) {
            showToast(error.response?.data?.message || 'Ошибка при отправке письма', 'error');
        } finally {
            setIsResending(false);
        }
    };

    const handleGoToProfile = () => {
        navigate('/profile');
    };

    const handleGoHome = () => {
        navigate('/');
    };

    const handleGoToLogin = () => {
        navigate('/login');
    };

    // Если есть токен в URL, проверяем его (для авторизованных и неавторизованных пользователей)
    const token = new URLSearchParams(location.search).get('token');
    if (token) {
        if (isVerifying) {
            return (
                <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                    <Card className="p-5 text-center" style={{ maxWidth: '600px', width: '100%' }}>
                        <Spinner animation="border" className="mx-auto mb-4 text-primary" />
                        <h2 className="mb-4">Подтверждение Email</h2>
                        <p>Пожалуйста, подождите, идет проверка...</p>
                    </Card>
                </Container>
            );
        }

        if (verificationStatus === 'success') {
            return (
                <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                    <Card className="p-5 text-center" style={{ maxWidth: '600px', width: '100%' }}>
                        <FaCheckCircle size={64} className="mx-auto mb-4 text-success" />
                        <h2 className="mb-4">Email подтвержден!</h2>
                        <p className="mb-4">Ваш email успешно подтвержден. Теперь вы можете пользоваться всеми функциями сайта.</p>
                        <Button variant="success" onClick={handleGoToProfile}>
                            Перейти в профиль
                        </Button>
                    </Card>
                </Container>
            );
        }

        if (verificationStatus === 'already-verified') {
            return (
                <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                    <Card className="p-5 text-center" style={{ maxWidth: '600px', width: '100%' }}>
                        <FaCheckCircle size={64} className="mx-auto mb-4 text-info" />
                        <h2 className="mb-4">Email уже подтвержден</h2>
                        <p className="mb-4">Этот email уже был подтвержден ранее.</p>
                        <Button variant="primary" onClick={handleGoToProfile}>
                            Перейти в профиль
                        </Button>
                    </Card>
                </Container>
            );
        }

        if (verificationStatus === 'error') {
            return (
                <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                    <Card className="p-5 text-center" style={{ maxWidth: '600px', width: '100%' }}>
                        <FaExclamationTriangle size={64} className="mx-auto mb-4 text-danger" />
                        <h2 className="mb-4">Ошибка подтверждения</h2>
                        <Alert variant="danger" className="mb-4">
                            {errorMessage}
                        </Alert>
                        <p className="mb-4">
                            Возможно, ссылка истекла или недействительна. Запросите новое письмо подтверждения.
                        </p>
                        <div className="d-flex justify-content-center gap-3">
                            <Button 
                                variant="primary" 
                                onClick={handleResendVerification}
                                disabled={isResending}
                            >
                                {isResending ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Отправка...
                                    </>
                                ) : (
                                    'Отправить письмо повторно'
                                )}
                            </Button>
                            <Button variant="outline-secondary" onClick={handleGoHome}>
                                Вернуться на главную
                            </Button>
                        </div>
                    </Card>
                </Container>
            );
        }
    }

    // Если пользователь авторизован и email уже подтвержден
    if (user.isAuth && user.user.is_verified && !verificationStatus) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                <Card className="p-5 text-center" style={{ maxWidth: '600px', width: '100%' }}>
                    <FaCheckCircle size={64} className="mx-auto mb-4 text-success" />
                    <h2 className="mb-4">Email уже подтвержден</h2>
                    <p className="mb-4">Ваш email уже подтвержден. Вы можете продолжить работу с сайтом.</p>
                    <Button variant="primary" onClick={handleGoToProfile}>
                        Перейти в профиль
                    </Button>
                </Card>
            </Container>
        );
    }

    // Основная страница для повторной отправки письма (работает для авторизованных и неавторизованных пользователей)
    return (
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
            <Card className="p-5 text-center" style={{ maxWidth: '600px', width: '100%' }}>
                <FaEnvelope size={64} className="mx-auto mb-4 text-primary" />
                <h2 className="mb-4">Подтверждение Email</h2>
                <p className="mb-4">
                    {user.isAuth && user.user.email ? (
                        <>
                            Мы отправили письмо с подтверждением на адрес <strong>{user.user.email}</strong>.
                            Пожалуйста, проверьте вашу почту и перейдите по ссылке для подтверждения.
                        </>
                    ) : (
                        <>
                            Введите ваш email для отправки письма с подтверждением.
                        </>
                    )}
                </p>
                <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                        type="email"
                        value={manualEmail}
                        onChange={e => setManualEmail(e.target.value)}
                        placeholder="example@mail.ru"
                        required
                    />
                </Form.Group>
                <div className="d-flex justify-content-center gap-3">
                    <Button 
                        variant="primary" 
                        onClick={handleResendVerification}
                        disabled={isResending}
                    >
                        {isResending ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Отправка...
                            </>
                        ) : (
                            'Отправить письмо повторно'
                        )}
                    </Button>
                    <Button variant="outline-secondary" onClick={handleGoHome}>
                        Вернуться на главную
                    </Button>
                </div>
                {!user.isAuth && (
                    <div className="mt-3">
                        <Button variant="link" onClick={handleGoToLogin}>
                            Уже есть аккаунт? Войти
                        </Button>
                    </div>
                )}
                <div className="mt-4 text-muted">
                    <small>
                        Если вы не получили письмо, проверьте папку "Спам" или нажмите кнопку "Отправить письмо повторно"
                    </small>
                </div>
            </Card>
        </Container>
    );
};

export default VerifyEmail; 